// src/pages/admin/DealsSliderAdmin.jsx
import { useEffect, useState } from 'react'
import {
  fetchAllDealsSlides,
  createDealsSlide,
  updateDealsSlide,
  deleteDealsSlide,
} from '../../lib/dealsSlider'
import { storage } from '../../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  kicker: '',
  badge: '',
  ctaLabel: 'Voir les offres',
  ctaHref: '/catalog',
  leftImg: '',
  leftAlt: '',
  rightImg: '',
  rightAlt: '',
  bgColor: '#002b7f',
  order: 0,
  isActive: true,
}

export default function DealsSliderAdmin() {
  const [slides, setSlides] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [editing, setEditing] = useState(null) // slide en édition (ou null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const [isFormOpen, setIsFormOpen] = useState(false)

  // états d’upload images
  const [uploadingLeft, setUploadingLeft] = useState(false)
  const [uploadingRight, setUploadingRight] = useState(false)

  // Charger les slides
  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const rows = await fetchAllDealsSlides()
      setSlides(rows)
    } catch (e) {
      console.error(e)
      setError(e?.message || 'Erreur de chargement des slides.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  // Ouvrir le formulaire (nouveau)
  const openNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setIsFormOpen(true)
  }

  // Ouvrir le formulaire (édition)
  const openEdit = (slide) => {
    setEditing(slide)
    setForm({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      kicker: slide.kicker || '',
      badge: slide.badge || '',
      ctaLabel: slide.ctaLabel || '',
      ctaHref: slide.ctaHref || '',
      leftImg: slide.leftImg || '',
      leftAlt: slide.leftAlt || '',
      rightImg: slide.rightImg || '',
      rightAlt: slide.rightAlt || '',
      bgColor: slide.bgColor || '#002b7f',
      order: slide.order || 0,
      isActive: !!slide.isActive,
    })
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
    setUploadingLeft(false)
    setUploadingRight(false)
  }

  // Upload générique
  const handleImageUpload = async (file, side) => {
    if (!file) return
    try {
      if (side === 'left') setUploadingLeft(true)
      if (side === 'right') setUploadingRight(true)

      const path = `dealsSlider/${Date.now()}_${file.name}`
      const fileRef = ref(storage, path)
      await uploadBytes(fileRef, file)
      const url = await getDownloadURL(fileRef)

      setForm((prev) => ({
        ...prev,
        [side === 'left' ? 'leftImg' : 'rightImg']: url,
      }))
    } catch (e) {
      console.error(e)
      alert("Erreur lors de l'upload de l'image.")
    } finally {
      if (side === 'left') setUploadingLeft(false)
      if (side === 'right') setUploadingRight(false)
    }
  }

  const onLeftFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) handleImageUpload(file, 'left')
  }

  const onRightFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) handleImageUpload(file, 'right')
  }

  // Submit formulaire
  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editing) {
        await updateDealsSlide(editing.id, form)
      } else {
        await createDealsSlide(form)
      }
      await load()
      closeForm()
    } catch (e) {
      console.error(e)
      alert("Erreur lors de l'enregistrement du slide.")
    } finally {
      setSaving(false)
    }
  }

  // Delete
  const confirmDelete = (slide) => {
    setDeletingId(slide.id)
    if (window.confirm('Supprimer ce slide ?')) {
      handleDelete(slide.id)
    } else {
      setDeletingId(null)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteDealsSlide(id)
      await load()
    } catch (e) {
      console.error(e)
      alert('Erreur lors de la suppression.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  return (
    <div className="my-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Admin — Deals Slider</h3>
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          + Nouveau slide
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="alert alert-info">Chargement des slides…</div>
      ) : slides.length === 0 ? (
        <div className="alert alert-light border">
          Aucun slide. Ajoute ton premier “deal” avec le bouton
          <strong> Nouveau slide</strong>.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Ordre</th>
                <th>Titre</th>
                <th>Badge</th>
                <th>CTA</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slides.map((s, idx) => (
                <tr key={s.id}>
                  <td>{idx + 1}</td>
                  <td>{s.order}</td>
                  <td>
                    <div className="fw-semibold">
                      {s.title || '(sans titre)'}
                    </div>
                    <div className="small text-muted">
                      {s.kicker || s.subtitle}
                    </div>
                  </td>
                  <td>
                    {s.badge && (
                      <span className="badge bg-warning text-dark">
                        {s.badge}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="small">{s.ctaLabel}</div>
                    <div className="small text-muted">{s.ctaHref}</div>
                  </td>
                  <td>
                    {s.isActive ? (
                      <span className="badge bg-success">Oui</span>
                    ) : (
                      <span className="badge bg-secondary">Non</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEdit(s)}
                    >
                      Éditer
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => confirmDelete(s)}
                      disabled={deletingId === s.id}
                    >
                      {deletingId === s.id ? 'Suppression…' : 'Supprimer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🧾 FORMULAIRE INLINE */}
      {isFormOpen && (
        <div className="card card-modern mt-3">
          <div className="card-body">
            <h5 className="card-title mb-3">
              {editing ? 'Modifier le slide' : 'Nouveau slide deals'}
            </h5>

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Titre principal</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Ordre</label>
                  <input
                    type="number"
                    name="order"
                    className="form-control"
                    value={form.order}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Kicker (petit texte haut)
                  </label>
                  <input
                    type="text"
                    name="kicker"
                    className="form-control"
                    value={form.kicker}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Badge (ex: “Nov. 25-30 only”)
                  </label>
                  <input
                    type="text"
                    name="badge"
                    className="form-control"
                    value={form.badge}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Sous-titre</label>
                  <input
                    type="text"
                    name="subtitle"
                    className="form-control"
                    value={form.subtitle}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Texte du bouton (CTA)</label>
                  <input
                    type="text"
                    name="ctaLabel"
                    className="form-control"
                    value={form.ctaLabel}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Lien du bouton</label>
                  <input
                    type="text"
                    name="ctaHref"
                    className="form-control"
                    value={form.ctaHref}
                    onChange={handleChange}
                  />
                </div>

                <hr className="mt-3" />

                {/* 🔼 IMAGE GAUCHE – UPLOAD */}
                <div className="col-md-6">
                  <label className="form-label">Image gauche</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={onLeftFileChange}
                  />
                  {uploadingLeft && (
                    <div className="form-text text-primary">
                      Upload en cours…
                    </div>
                  )}
                  {form.leftImg && !uploadingLeft && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img
                        src={form.leftImg}
                        alt="Preview gauche"
                        style={{
                          width: 90,
                          height: 90,
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                      />
                      <span className="small text-muted">
                        Image uploadée.
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Alt image gauche</label>
                  <input
                    type="text"
                    name="leftAlt"
                    className="form-control"
                    value={form.leftAlt}
                    onChange={handleChange}
                  />
                </div>

                {/* 🔼 IMAGE DROITE – UPLOAD */}
                <div className="col-md-6">
                  <label className="form-label">Image droite</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={onRightFileChange}
                  />
                  {uploadingRight && (
                    <div className="form-text text-primary">
                      Upload en cours…
                    </div>
                  )}
                  {form.rightImg && !uploadingRight && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img
                        src={form.rightImg}
                        alt="Preview droite"
                        style={{
                          width: 90,
                          height: 90,
                          objectFit: 'cover',
                          borderRadius: 8,
                        }}
                      />
                      <span className="small text-muted">
                        Image uploadée.
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Alt image droite</label>
                  <input
                    type="text"
                    name="rightAlt"
                    className="form-control"
                    value={form.rightAlt}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Couleur de fond (hex)</label>
                  <input
                    type="text"
                    name="bgColor"
                    className="form-control"
                    value={form.bgColor}
                    onChange={handleChange}
                  />
                  <div className="form-text">
                    Ex : <code>#002b7f</code> pour bleu nuit.
                  </div>
                </div>

                <div className="col-md-6 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActiveCheck"
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="isActiveCheck"
                    >
                      Slide actif (visible sur le site)
                    </label>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeForm}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || uploadingLeft || uploadingRight}
                >
                  {saving
                    ? 'Enregistrement…'
                    : editing
                    ? 'Enregistrer les modifications'
                    : 'Créer le slide'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
